import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Alert,
  Button,
  TextField,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Info as InfoIcon,
  Calculate as CalculateIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,

} from '@mui/icons-material';
import { calculatePositionSize, formatCurrency, formatPercent } from '../utils';

const RiskManagement = ({ trades, analysis }) => {
  const [accountBalance, setAccountBalance] = useState(10000);
  const [riskPercent, setRiskPercent] = useState(1);
  const [entryPrice, setEntryPrice] = useState(15500);
  const [stopLoss, setStopLoss] = useState(15480);
  const [positionResult, setPositionResult] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const detailedAnalysis = analysis;

  // Risk management alerts
  useEffect(() => {
    const newAlerts = [];
    
    // Drawdown alert
    if (detailedAnalysis.maxDrawdownPercent > 10) {
      newAlerts.push({
        type: 'error',
        title: 'Significant Drawdown',
        message: `Maximum drawdown of ${formatPercent(detailedAnalysis.maxDrawdownPercent)} exceeds recommended 10% threshold.`,
        severity: 'high',
      });
    } else if (detailedAnalysis.maxDrawdownPercent > 5) {
      newAlerts.push({
        type: 'warning',
        title: 'Moderate Drawdown',
        message: `Maximum drawdown of ${formatPercent(detailedAnalysis.maxDrawdownPercent)} is approaching recommended limits.`,
        severity: 'medium',
      });
    }
    
    // Consecutive losses alert
    if (detailedAnalysis.consecutiveLosses >= 3) {
      newAlerts.push({
        type: 'warning',
        title: 'Consecutive Losses',
        message: `${detailedAnalysis.consecutiveLosses} consecutive losses detected. Consider taking a break or reviewing your strategy.`,
        severity: 'high',
      });
    }
    
    // Risk/reward alert
    if (detailedAnalysis.avgRiskReward < 1.5) {
      newAlerts.push({
        type: 'warning',
        title: 'Low Risk/Reward Ratio',
        message: `Average risk/reward ratio of ${(detailedAnalysis.avgRiskReward || 0).toFixed(2)} is below recommended 2:1 minimum.`,
        severity: 'medium',
      });
    }
    
    // Position sizing alert
    if (detailedAnalysis.qtyStd > 0.5) {
      newAlerts.push({
        type: 'warning',
        title: 'Inconsistent Position Sizing',
        message: `Position size standard deviation of ${(detailedAnalysis.qtyStd || 0).toFixed(2)} indicates inconsistent risk management.`,
        severity: 'medium',
      });
    }
    
    setAlerts(newAlerts);
  }, [trades, detailedAnalysis]);

  const handleCalculatePosition = () => {
    const result = calculatePositionSize(
      parseFloat(accountBalance),
      parseFloat(riskPercent),
      parseFloat(entryPrice),
      parseFloat(stopLoss)
    );
    setPositionResult(result);
  };

  const handleQuickCalculate = (balance, risk, entry, sl) => {
    setAccountBalance(balance);
    setRiskPercent(risk);
    setEntryPrice(entry);
    setStopLoss(sl);
    
    setTimeout(() => {
      const result = calculatePositionSize(
        parseFloat(balance),
        parseFloat(risk),
        parseFloat(entry),
        parseFloat(sl)
      );
      setPositionResult(result);
    }, 100);
  };

  // Risk metrics
  const riskMetrics = [
    {
      title: 'Max Drawdown',
      value: formatCurrency(detailedAnalysis.maxDrawdown),
      description: `${formatPercent(detailedAnalysis.maxDrawdownPercent)} of peak equity`,
      status: detailedAnalysis.maxDrawdownPercent > 10 ? 'danger' :
               detailedAnalysis.maxDrawdownPercent > 5 ? 'warning' : 'success',
    },
    {
      title: 'Consecutive Losses',
      value: detailedAnalysis.consecutiveLosses,
      description: 'Current losing streak',
      status: detailedAnalysis.consecutiveLosses >= 3 ? 'danger' :
               detailedAnalysis.consecutiveLosses >= 2 ? 'warning' : 'success',
    },
    {
      title: 'Position Consistency',
      value: (detailedAnalysis.qtyStd || 0).toFixed(2),
      description: 'Standard deviation of position sizes',
      status: detailedAnalysis.qtyStd > 0.5 ? 'warning' : 'success',
    },
    {
      title: 'Avg Risk/Reward',
      value: (detailedAnalysis.avgRiskReward || 0).toFixed(2),
      description: 'Average reward to risk ratio',
      status: detailedAnalysis.avgRiskReward < 1.5 ? 'warning' :
               detailedAnalysis.avgRiskReward < 2 ? 'neutral' : 'success',
    },
  ];

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" gutterBottom>
        Risk Management
      </Typography>
      
      {/* Risk Alerts */}
      {alerts.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Risk Alerts
          </Typography>
          <Grid container spacing={2}>
            {alerts.map((alert, index) => (
              <Grid item xs={12} key={index}>
                <Alert
                  severity={alert.type === 'error' ? 'error' : 'warning'}
                  icon={<WarningIcon />}
                >
                  <strong>{alert.title}:</strong> {alert.message}
                </Alert>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
      
      <Grid container spacing={3}>
        {/* Risk Metrics */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              avatar={<TrendingDownIcon color="error" />}
              title="Risk Metrics"
              subheader="Key risk indicators from your trading history"
            />
            <CardContent>
              <Grid container spacing={2}>
                {riskMetrics.map((metric, index) => (
                  <Grid item xs={6} sm={3} key={index}>
                    <Paper
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        borderLeft: `4px solid ${
                          metric.status === 'danger' ? '#f44336' :
                          metric.status === 'warning' ? '#ff9800' :
                          metric.status === 'success' ? '#4caf50' : '#2196f3'
                        }`,
                      }}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        {metric.title}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color:
                            metric.status === 'danger' ? 'error.main' :
                            metric.status === 'warning' ? 'warning.main' :
                            metric.status === 'success' ? 'success.main' : 'primary.main',
                          fontWeight: 'bold',
                        }}
                      >
                        {metric.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {metric.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Position Size Calculator */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardHeader
              avatar={<CalculateIcon color="primary" />}
              title="Position Size Calculator"
              subheader="Calculate optimal position size based on risk"
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Balance"
                    type="number"
                    value={accountBalance}
                    onChange={(e) => setAccountBalance(e.target.value)}
                    InputProps={{
                      startAdornment: <WalletIcon position="start" />,
                      endAdornment: '$',
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Risk Percentage"
                    type="number"
                    value={riskPercent}
                    onChange={(e) => setRiskPercent(e.target.value)}
                    InputProps={{
                      endAdornment: '%',
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Entry Price"
                    type="number"
                    value={entryPrice}
                    onChange={(e) => setEntryPrice(e.target.value)}
                    inputProps={{ step: '0.1' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Stop Loss"
                    type="number"
                    value={stopLoss}
                    onChange={(e) => setStopLoss(e.target.value)}
                    inputProps={{ step: '0.1' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleCalculatePosition}
                    startIcon={<CalculateIcon />}
                    fullWidth
                  >
                    Calculate Position Size
                  </Button>
                </Grid>
                
                {/* Quick presets */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Quick Presets:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label="$10k @ 1%"
                      onClick={() => handleQuickCalculate(10000, 1, 15500, 15480)}
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label="$25k @ 1%"
                      onClick={() => handleQuickCalculate(25000, 1, 15500, 15480)}
                      variant="outlined"
                      size="small"
                    />
                    <Chip
                      label="$50k @ 0.5%"
                      onClick={() => handleQuickCalculate(50000, 0.5, 15500, 15480)}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Grid>
                
                {/* Results */}
                {positionResult && (
                  <Grid item xs={12}>
                    <Alert severity="success">
                      <Typography variant="subtitle2">
                        Position Size Calculation Results:
                      </Typography>
                      <Typography variant="body2">
                        <strong>Position Size:</strong> {positionResult.positionSize} lots
                      </Typography>
                      <Typography variant="body2">
                        <strong>Risk Amount:</strong> {formatCurrency(positionResult.riskAmount)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Points at Risk:</strong> {positionResult.pointsRisk}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Risk Management Tips */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardHeader
              avatar={<InfoIcon color="info" />}
              title="Risk Management Tips"
              subheader="Best practices for consistent trading"
            />
            <CardContent>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <TrendingUpIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Risk Management Rule"
                    secondary="Never risk more than 1-2% of your account per trade"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <TrendingUpIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Position Sizing"
                    secondary="Use consistent position sizing to maintain risk control"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <TrendingUpIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Risk/Reward Ratio"
                    secondary="Aim for minimum 2:1 risk/reward on all trades"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <TrendingUpIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Drawdown Control"
                    secondary="Consider taking a break after 3 consecutive losses"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <TrendingUpIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Emotional Control"
                    secondary="Stick to your trading plan and avoid revenge trading"
                  />
                </ListItem>
              </List>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Risk Profile:
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, detailedAnalysis.maxDrawdownPercent * 10)}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor:
                        detailedAnalysis.maxDrawdownPercent > 10 ? '#f44336' :
                        detailedAnalysis.maxDrawdownPercent > 5 ? '#ff9800' : '#4caf50',
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption">
                    Drawdown: {formatPercent(detailedAnalysis.maxDrawdownPercent)}
                  </Typography>
                  <Typography variant="caption">
                    Status: {
                      detailedAnalysis.maxDrawdownPercent > 10 ? 'High Risk' :
                      detailedAnalysis.maxDrawdownPercent > 5 ? 'Moderate Risk' : 'Low Risk'
                    }
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default RiskManagement;